import { Alert, AlertDescription, AlertIcon, AlertTitle, Box, Button, HStack, ListItem, UnorderedList, Wrap, WrapItem, useToast } from '@chakra-ui/react';
import { MdAutoAwesome, MdContentCopy } from 'react-icons/md';
import type { AIChatMessageMetadata } from '@/lib/sdk/ai-chat/types';
import { linkCitations, plainCitations } from '../citations';
import { GuideMarkdown } from './GuideMarkdown';

interface PlanCardProps {
  /** The plan as Markdown, with [[entry-id]] citations */
  content: string;
  metadata: AIChatMessageMetadata;
  titles: Record<string, string>;
  onOpenEntry: (entryId: string) => void;
  /** Opens Build mode with a workflow's brief; absent when the user cannot build */
  onBuild?: (brief: string) => void;
}

/**
 * A Solve plan: master data, workflows and their tasks, why, the alternative, gaps and
 * open questions, with citations linking to the guide. It can be copied as Markdown for
 * a proposal, and each workflow handed to Build.
 */
export function PlanCard({ content, metadata, titles, onOpenEntry, onBuild }: PlanCardProps) {
  const toast = useToast();
  const workflows = metadata.plan?.workflows ?? [];
  const briefs = metadata.build_briefs ?? [];

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(plainCitations(content, titles));
      toast({ status: 'success', title: 'Plan copied as Markdown', duration: 2000 });
    } catch (error) {
      toast({ status: 'error', title: 'The plan could not be copied', description: error instanceof Error ? error.message : undefined });
    }
  };

  return (
    <Box borderWidth="1px" borderColor="border-default" borderRadius="md" px={4} py={3} data-testid="solve-plan">
      {metadata.plan_issues && metadata.plan_issues.length > 0 && (
        <Alert status="warning" borderRadius="md" mb={3} alignItems="flex-start" data-testid="solve-plan-issues">
          <AlertIcon />
          <Box>
            <AlertTitle fontSize="sm">Check these with the product team</AlertTitle>
            <AlertDescription fontSize="sm">
              <UnorderedList>
                {metadata.plan_issues.map((issue) => (
                  <ListItem key={issue}>{issue}</ListItem>
                ))}
              </UnorderedList>
            </AlertDescription>
          </Box>
        </Alert>
      )}
      <GuideMarkdown content={linkCitations(content, titles)} onOpenEntry={onOpenEntry} fontSize="sm" />
      <HStack spacing={2} mt={2} flexWrap="wrap">
        <Button size="sm" variant="outline" leftIcon={<MdContentCopy />} onClick={copy} data-testid="solve-plan-copy">
          Copy as Markdown
        </Button>
        {onBuild && (
          <Wrap spacing={2}>
            {briefs.map((brief, index) => (
              <WrapItem key={index}>
                <Button size="sm" leftIcon={<MdAutoAwesome />} onClick={() => onBuild(brief)} data-testid="solve-plan-build">
                  {briefs.length > 1 ? `Build ${workflows[index]?.title ?? `workflow ${index + 1}`}` : 'Build this'}
                </Button>
              </WrapItem>
            ))}
          </Wrap>
        )}
      </HStack>
    </Box>
  );
}
